import React, { useState, useCallback } from 'react';
import { DropdownMenuItem } from '../../ui/dropdown-menu';
import { useLazyCommand, useEvent, useDomainEvent } from '@soap/modules';
import { UserX, X } from 'lucide-react';

interface RevokeOrgInviteDialogProps {
  inviteId: string;
  email: string;
}

export function RevokeOrgInviteDialog({ inviteId, email }: RevokeOrgInviteDialogProps) {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [sendCommand] = useLazyCommand('C249v1_RevokeOrgUserInvitation');
  const triggerCloseContextMenu = useDomainEvent({
    eventName: "close-context-menu",
  });

  const close = useCallback(() => {
    setOpen(false);
    triggerCloseContextMenu();
  }, [triggerCloseContextMenu]);

  // Listen for successful revocation event
  useEvent({
    eventName: "E200v1_InvitationRevoked",
    onEventReceived: () => {
      close();
    }
  }, [close]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await sendCommand({
        C249_OrgUserInvitationId: inviteId
      });
    } catch (error) {
      console.error('Failed to revoke invitation:', error);
      setIsSubmitting(false);
    }
  };

  if (!open) {
    return (
      <DropdownMenuItem
        className="w-full justify-start"
        data-dialog-item
        onClick={() => setOpen(true)}
      >
        <UserX className="w-4 h-4 mr-2" />
        Revoke
      </DropdownMenuItem>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-5">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-2xl font-bold text-gray-900">Revoke User Invitation</h2>
            <button
              onClick={close}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={isSubmitting}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-4">
            <p className="text-gray-700">
              Please confirm you want to revoke this invitation for <strong>{email}</strong>?
            </p>
            
            <div className="bg-destructive-action/10 p-4 rounded-lg border border-destructive-action/30">
              <h4 className="font-medium text-destructive-action-dark mb-2">What happens when you revoke an invitation?</h4>
              <ul className="ml-4 space-y-1">
                <li className="text-sm text-destructive-action-dark">• The invitation link will no longer be valid</li>
                <li className="text-sm text-destructive-action-dark">• The user will not be able to accept the invitation</li>
                <li className="text-sm text-destructive-action-dark">• You can send a new invitation if needed</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-5">
            <button
              type="button"
              onClick={close}
              disabled={isSubmitting}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="px-6 py-2 bg-destructive-action hover:bg-destructive-action-dark text-white rounded-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed min-w-[140px]"
            >
              {isSubmitting ? "Revoking..." : "Revoke Invitation"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

